// Master Data Collection inferences, previews and timelines.
import {
  MasterDataCollectionCleaningToolStateSaveRequest,
  MasterDataCollectionCleaningToolStateSaveResponse,
  MasterDataCollectionDetailResponse,
  MasterDataCollectionImagePreviewResponse,
} from '../../types/masterDataCollection';
import {
  RemoteDataCollectionDailyPreviewsResponse,
  RemoteDataCollectionImageTimelineResponse,
  RemoteDataCollectionReviewTargetsResponse,
  RemoteDcCollectedCropsResponse,
} from '../../types/remoteDc';
import { forgeRequest } from '../forgeRequest';
import {
  FORGE_MASTER_DATA_COLLECTION_API_URL,
  FORGE_REMOTE_DATA_COLLECTION_API_URL,
} from './endpoints';

/** Product crops already cut from one camera Image, so the tool can restore them on reopen. */
export async function listRemoteDcCollectedCrops(
  token: string,
  params: { chainId: number; imageId: number },
  options?: { signal?: AbortSignal }
): Promise<RemoteDcCollectedCropsResponse> {
  const query = new URLSearchParams({
    chain_id: String(params.chainId),
    image_id: String(params.imageId),
  });
  return forgeRequest<RemoteDcCollectedCropsResponse>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/collected-crops?${query.toString()}`,
    'Error fetching collected crops',
    { signal: options?.signal }
  );
}

export async function fetchMasterDataCollectionInference(
  token: string,
  inferenceId: string,
  params?: {
    chainId?: number | null;
    itemNumber?: string | null;
    normalizedBarcode?: string | null;
    similarityThreshold?: number;
    deltaThreshold?: number;
    ssmConfidenceThreshold?: number | null;
    lowEmbeddingThreshold?: number | null;
    mergeLinkedCandidates?: boolean;
    nonAdminPreview?: boolean;
    signal?: AbortSignal;
  }
): Promise<MasterDataCollectionDetailResponse> {
  const query = new URLSearchParams();
  if (params?.chainId) query.set('chain_id', String(params.chainId));
  if (params?.itemNumber) query.set('item_number', params.itemNumber);
  if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
  if (params?.similarityThreshold !== undefined) {
    query.set('similarity_threshold', String(params.similarityThreshold));
  }
  if (params?.deltaThreshold !== undefined) {
    query.set('delta_threshold', String(params.deltaThreshold));
  }
  if (params?.ssmConfidenceThreshold !== undefined && params.ssmConfidenceThreshold !== null) {
    query.set('ssm_confidence_threshold', String(params.ssmConfidenceThreshold));
  }
  if (params?.lowEmbeddingThreshold !== undefined && params.lowEmbeddingThreshold !== null) {
    query.set('low_embedding_threshold', String(params.lowEmbeddingThreshold));
  }
  if (params?.mergeLinkedCandidates !== undefined) {
    query.set('merge_linked_candidates', String(params.mergeLinkedCandidates));
  }
  if (params?.nonAdminPreview) query.set('non_admin_preview', 'true');
  return forgeRequest<MasterDataCollectionDetailResponse>(
    token,
    `${FORGE_MASTER_DATA_COLLECTION_API_URL}/inferences/${encodeURIComponent(
      inferenceId
    )}${query.toString() ? `?${query.toString()}` : ''}`,
    'Error fetching Master Data Collection inference',
    { signal: params?.signal }
  );
}

export async function fetchMasterDataCollectionReviewTargets(
  token: string,
  params: {
    chainId: number;
    itemNumber?: string | null;
    normalizedBarcode?: string | null;
    maxStores?: number;
  }
): Promise<RemoteDataCollectionReviewTargetsResponse> {
  const query = new URLSearchParams();
  query.set('chain_id', String(params.chainId));
  if (params.itemNumber) query.set('item_number', params.itemNumber);
  if (params.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
  if (params.maxStores !== undefined) query.set('max_stores', String(params.maxStores));

  return forgeRequest<RemoteDataCollectionReviewTargetsResponse>(
    token,
    `${FORGE_MASTER_DATA_COLLECTION_API_URL}/review-targets?${query.toString()}`,
    'Error fetching Remote DC review targets'
  );
}

export async function fetchMasterDataCollectionImagePreview(
  token: string,
  imageId: number,
  params?: {
    chainId?: number | null;
    itemNumber?: string | null;
    normalizedBarcode?: string | null;
    signal?: AbortSignal;
  }
): Promise<MasterDataCollectionImagePreviewResponse> {
  const query = new URLSearchParams();
  if (params?.chainId) query.set('chain_id', String(params.chainId));
  if (params?.itemNumber) query.set('item_number', params.itemNumber);
  if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);

  return forgeRequest<MasterDataCollectionImagePreviewResponse>(
    token,
    `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
      String(imageId)
    )}/preview${query.toString() ? `?${query.toString()}` : ''}`,
    'Error fetching Remote DC image preview',
    { signal: params?.signal }
  );
}

export async function fetchMasterDataCollectionImageTimeline(
  token: string,
  imageId: number,
  params?: {
    chainId?: number | null;
    itemNumber?: string | null;
    normalizedBarcode?: string | null;
    previousCount?: number;
    signal?: AbortSignal;
  }
): Promise<RemoteDataCollectionImageTimelineResponse> {
  const query = new URLSearchParams();
  if (params?.chainId) query.set('chain_id', String(params.chainId));
  if (params?.itemNumber) query.set('item_number', params.itemNumber);
  if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
  if (params?.previousCount !== undefined)
    query.set('previous_count', String(params.previousCount));

  return forgeRequest<RemoteDataCollectionImageTimelineResponse>(
    token,
    `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
      String(imageId)
    )}/timeline${query.toString() ? `?${query.toString()}` : ''}`,
    'Error fetching Remote DC image timeline',
    { signal: params?.signal }
  );
}

export async function loadMasterDataCollectionImageInference(
  token: string,
  imageId: number,
  params?: {
    chainId?: number | null;
    itemNumber?: string | null;
    normalizedBarcode?: string | null;
    similarityThreshold?: number;
    deltaThreshold?: number;
    ssmConfidenceThreshold?: number | null;
    lowEmbeddingThreshold?: number | null;
    mergeLinkedCandidates?: boolean;
    nonAdminPreview?: boolean;
    cachedOnly?: boolean;
    signal?: AbortSignal;
  }
): Promise<MasterDataCollectionDetailResponse> {
  const query = new URLSearchParams();
  if (params?.chainId) query.set('chain_id', String(params.chainId));
  if (params?.itemNumber) query.set('item_number', params.itemNumber);
  if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
  if (params?.similarityThreshold !== undefined) {
    query.set('similarity_threshold', String(params.similarityThreshold));
  }
  if (params?.deltaThreshold !== undefined) {
    query.set('delta_threshold', String(params.deltaThreshold));
  }
  if (params?.ssmConfidenceThreshold !== undefined && params.ssmConfidenceThreshold !== null) {
    query.set('ssm_confidence_threshold', String(params.ssmConfidenceThreshold));
  }
  if (params?.lowEmbeddingThreshold !== undefined && params.lowEmbeddingThreshold !== null) {
    query.set('low_embedding_threshold', String(params.lowEmbeddingThreshold));
  }
  if (params?.mergeLinkedCandidates !== undefined) {
    query.set('merge_linked_candidates', String(params.mergeLinkedCandidates));
  }
  if (params?.nonAdminPreview) query.set('non_admin_preview', 'true');
  if (params?.cachedOnly !== undefined) query.set('cached_only', String(params.cachedOnly));
  return forgeRequest<MasterDataCollectionDetailResponse>(
    token,
    `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
      String(imageId)
    )}/load-inference${query.toString() ? `?${query.toString()}` : ''}`,
    'Error loading Remote DC product recognition',
    { method: 'POST', signal: params?.signal }
  );
}

export async function fetchMasterDataCollectionDailyPreviews(
  token: string,
  imageId: number,
  params?: {
    chainId?: number | null;
    itemNumber?: string | null;
    normalizedBarcode?: string | null;
    daysBefore?: number;
    daysAfter?: number;
  }
): Promise<RemoteDataCollectionDailyPreviewsResponse> {
  const query = new URLSearchParams();
  if (params?.chainId) query.set('chain_id', String(params.chainId));
  if (params?.itemNumber) query.set('item_number', params.itemNumber);
  if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
  if (params?.daysBefore !== undefined) query.set('days_before', String(params.daysBefore));
  if (params?.daysAfter !== undefined) query.set('days_after', String(params.daysAfter));

  return forgeRequest<RemoteDataCollectionDailyPreviewsResponse>(
    token,
    `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
      String(imageId)
    )}/daily-previews${query.toString() ? `?${query.toString()}` : ''}`,
    'Error fetching Remote DC image previews'
  );
}

export async function saveMasterDataCollectionCleaningToolState(
  token: string,
  inferenceId: string,
  request: MasterDataCollectionCleaningToolStateSaveRequest
): Promise<MasterDataCollectionCleaningToolStateSaveResponse> {
  return forgeRequest<MasterDataCollectionCleaningToolStateSaveResponse>(
    token,
    `${FORGE_MASTER_DATA_COLLECTION_API_URL}/inferences/${encodeURIComponent(
      inferenceId
    )}/cleaning-tool-state`,
    'Error saving Master Data Collection Cleaning state',
    { method: 'PUT', body: request }
  );
}
